import React, { useState, useEffect, useContext } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import { withAuth } from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import { FormattedMessage } from "react-intl";
import MessageContext from "../../MessageContext";
import Loader from "../../Components/Utils/Loader";

import classes from "./styles.module.css";

var ACCESS_ANTIFRAUD_SINGLE = 65536;

var INTERNAL_PROVIDERS = ["tkb", "finstar", "manual", "nx"];
var EXTERNAL_PROVIDERS = ["scanfraud"];

var STATUS_LABELS = {
    ok: "Не найдено",
    blocked: "Найдено",
    skipped: "Пропущено",
    error: "Ошибка",
};

var KIND_LABELS = {
    card_pan: "Карта",
    phone: "Телефон",
    sbp_phone: "Телефон (СБП)",
    inn: "ИНН",
    ip: "IP",
};

/**
 * Объединяет результаты провайдеров в 2 группы: Внутренняя / Внешняя.
 * Для каждой группы берётся "худший" статус: blocked > error > ok > skipped.
 */
function mergeProviderGroups(providers) {
    if (!providers) return [];

    var groups = [
        { label: "Внутренняя", codes: INTERNAL_PROVIDERS },
        { label: "Внешняя", codes: EXTERNAL_PROVIDERS },
    ];

    var statusPriority = { blocked: 4, error: 3, ok: 2, skipped: 1 };

    var merged = [];
    for (var g = 0; g < groups.length; g++) {
        var group = groups[g];
        var bestStatus = null;
        var bestPriority = 0;
        var details = [];
        var totalMs = 0;
        var hasAny = false;

        for (var i = 0; i < group.codes.length; i++) {
            var code = group.codes[i];
            var p = providers[code];
            if (!p) continue;
            hasAny = true;
            var prio = statusPriority[p.status] || 0;
            if (prio > bestPriority) {
                bestPriority = prio;
                bestStatus = p.status;
            }
            totalMs += (p.ms || 0);
            if (p.status === "blocked") {
                var kindLabel = p.kind ? (KIND_LABELS[p.kind] || p.kind) : "";
                details.push(kindLabel + (kindLabel && p.reason ? ": " : "") + (p.reason || ""));
            }
        }

        if (!hasAny) continue;

        merged.push({
            label: group.label,
            status: bestStatus || "skipped",
            details: details.length > 0 ? details.join("; ") : "—",
            ms: totalMs,
        });
    }
    return merged;
}

export default withAuth(function ({ token, accountAccessSettings }) {
    var msgCtx = useContext(MessageContext);
    var onMessage = msgCtx.onMessage;

    var _pu = useState(null);
    var projectUUID = _pu[0];
    var setProjectUUID = _pu[1];

    var _f = useState({ cardPan: "", phone: "", inn: "", clientIp: "" });
    var form = _f[0];
    var setForm = _f[1];

    var _r = useState(null);
    var result = _r[0];
    var setResult = _r[1];

    var _l = useState(false);
    var isChecking = _l[0];
    var setIsChecking = _l[1];

    var _lp = useState(true);
    var loadingProjects = _lp[0];
    var setLoadingProjects = _lp[1];

    // Load projects and auto-select first with ACCESS_ANTIFRAUD_SINGLE permission
    useEffect(function () {
        var cancelled = false;
        new Api({ token }).getProjects().then(function (data) {
            if (cancelled) return;
            var list = Array.isArray(data) ? data : [];
            var settings = accountAccessSettings || [];

            var found = null;
            for (var i = 0; i < list.length; i++) {
                var p = list[i];
                for (var j = 0; j < settings.length; j++) {
                    if (settings[j].merchantUUID === p.uuid && (settings[j].privileges & ACCESS_ANTIFRAUD_SINGLE) > 0) {
                        found = p.uuid;
                        break;
                    }
                }
                if (found) break;
            }

            if (!found && list.length > 0) {
                found = list[0].uuid;
            }

            if (found) {
                setProjectUUID(found);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
            if (!cancelled) onMessage({ type: "error", message: "Ошибка загрузки проектов" });
        }).finally(function () {
            if (!cancelled) setLoadingProjects(false);
        });
        return function () { cancelled = true; };
    }, [token, accountAccessSettings]);

    function onFieldChange(field, value) {
        setForm(function (prev) {
            var copy = Object.assign({}, prev);
            copy[field] = value;
            return copy;
        });
        setResult(null);
    }

    function hasAnyField() {
        return form.cardPan.trim() !== ""
            || form.phone.trim() !== ""
            || form.inn.trim() !== ""
            || form.clientIp.trim() !== "";
    }

    function handleSubmit() {
        if (!projectUUID) {
            onMessage({ type: "error", message: "Нет доступных проектов" });
            return;
        }
        if (!hasAnyField()) {
            onMessage({ type: "error", message: "Заполните хотя бы одно поле для проверки" });
            return;
        }

        setIsChecking(true);
        setResult(null);

        var payload = {};
        if (form.cardPan.trim()) payload.cardPan = form.cardPan.trim();
        if (form.phone.trim()) payload.phone = form.phone.trim();
        if (form.inn.trim()) payload.inn = form.inn.trim();
        if (form.clientIp.trim()) payload.clientIp = form.clientIp.trim();

        new Api({ token }).antifraudSingleCheck(projectUUID, payload).then(function (data) {
            setResult(data);
        }).catch(function (e) {
            Sentry.captureException(e);
            var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка проверки";
            onMessage({ type: "error", message: msg });
        }).finally(function () {
            setIsChecking(false);
        });
    }

    function clearForm() {
        setForm({ cardPan: "", phone: "", inn: "", clientIp: "" });
        setResult(null);
    }

    if (loadingProjects) {
        return (
            <Wrapper>
                <Container className={classes.StackOnMobile}>
                    <Column width="780" className={classes.MainColumn}>
                        <div className={classes.LoaderBox}>
                            <Loader />
                        </div>
                    </Column>
                </Container>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Container className={classes.StackOnMobile}>
                <Column width="780" className={classes.MainColumn}>
                            <div className={classes.Card}>
                                <h4 className={classes.Title}>
                                    <FormattedMessage id="UserMenu.title.antifraudSingleCheck"
                                                      defaultMessage="Разовая проверка"/>
                                </h4>

                                <div className={classes.FormGrid}>
                                    <div className={classes.Field}>
                                        <label className={classes.Label}>Номер карты</label>
                                        <input
                                            type="text"
                                            className={classes.Input}
                                            placeholder="4111111111111111"
                                            value={form.cardPan}
                                            onChange={function (e) {
                                                onFieldChange("cardPan", e.target.value);
                                            }}
                                        />
                                    </div>
                                    <div className={classes.Field}>
                                        <label className={classes.Label}>Телефон</label>
                                        <input
                                            type="text"
                                            className={classes.Input}
                                            placeholder="79001234567"
                                            value={form.phone}
                                            onChange={function (e) {
                                                onFieldChange("phone", e.target.value);
                                            }}
                                        />
                                    </div>
                                    <div className={classes.Field}>
                                        <label className={classes.Label}>ИНН</label>
                                        <input
                                            type="text"
                                            className={classes.Input}
                                            placeholder="7700000000"
                                            value={form.inn}
                                            onChange={function (e) {
                                                onFieldChange("inn", e.target.value);
                                            }}
                                        />
                                    </div>
                                    <div className={classes.Field}>
                                        <label className={classes.Label}>IP адрес</label>
                                        <input
                                            type="text"
                                            className={classes.Input}
                                            placeholder="192.168.1.1"
                                            value={form.clientIp}
                                            onChange={function (e) {
                                                onFieldChange("clientIp", e.target.value);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className={classes.Actions}>
                                    <button
                                        className={classes.BtnPrimary}
                                        onClick={handleSubmit}
                                        disabled={isChecking || !hasAnyField() || !projectUUID}
                                    >
                                        {isChecking ? <Loader isString/> : "Проверить"}
                                    </button>
                                    <button className={classes.BtnSecondary} onClick={clearForm}>
                                        Очистить
                                    </button>
                                </div>
                            </div>

                            {/* Result — таблица по всем провайдерам */}
                            {result && (
                                <div className={result.blocked ? classes.ResultBlocked : classes.ResultOk}>
                                    <div className={classes.ResultHeader}>
                                <span className={result.blocked ? classes.BadgeDanger : classes.BadgeSuccess}>
                                    {result.blocked ? "ЗАБЛОКИРОВАНО" : "OK"}
                                </span>
                                        <span
                                            className={classes.TimeBadge}>{(result.totalTimeMs / 1000).toFixed(2)} сек</span>
                                    </div>

                                    {result.providers && (
                                        <table className={classes.ProvidersTable}>
                                            <thead>
                                            <tr>
                                                <th>Проверка</th>
                                                <th>Результат</th>
                                                <th>Детали</th>
                                                <th>Время</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {mergeProviderGroups(result.providers).map(function (row) {
                                                var statusClass = row.status === "blocked" ? classes.StatusBlocked
                                                    : row.status === "ok" ? classes.StatusOk
                                                        : classes.StatusSkipped;
                                                return (
                                                    <tr key={row.label}>
                                                        <td>{row.label}</td>
                                                        <td className={statusClass}>
                                                            {STATUS_LABELS[row.status] || row.status}
                                                        </td>
                                                        <td>{row.details}</td>
                                                        <td>{(row.ms / 1000).toFixed(2)} сек</td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                </Column>

                <Column width="350" className={classes.SideColumn}>
                            <div className={classes.SideCard}>
                                <h6 className={classes.SideTitle}>Справка</h6>
                                <div className={classes.HelpText}>
                                    <p>Заполните одно или несколько полей для проверки по базам антифрода.</p>
                                    <ul>
                                        <li><strong>Номер карты</strong> — 13-19 цифр</li>
                                        <li><strong>Телефон</strong> — номер в любом формате</li>
                                        <li><strong>ИНН</strong> — 10 или 12 цифр</li>
                                        <li><strong>IP адрес</strong> — IPv4 или IPv6</li>
                                    </ul>
                                </div>
                            </div>
                </Column>
            </Container>
        </Wrapper>
    );
});



// WEBPACK FOOTER //
// ./src/PagesOfUser/UserAntiFraudSingleCheck/index.js